import React, { useState, useMemo } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { PerformanceFiltersState, PerformanceGroupBy, PerformanceSortKey } from '../../types';
import { useFinancePerformanceData } from '../../hooks/useFinancePerformanceData';
import { PerformanceFilters, PerformanceKpis, PerformanceTable } from '../components';
import { SharedFinanceFilters } from 'modules/finance/sharedFilters';

interface Props {
  sharedFilters: SharedFinanceFilters;
  onSharedFiltersChange: (filters: SharedFinanceFilters) => void;
}

export const FinancePerformancePage = ({ sharedFilters, onSharedFiltersChange }: Props) => {
  const [groupBy, setGroupBy] = useState<PerformanceGroupBy>('SALES_REP');
  const [sortKey, setSortKey] = useState<PerformanceSortKey>('totalProfit');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filters = useMemo<PerformanceFiltersState>(
    () => ({
      groupBy,
      dateRange: sharedFilters.dateRange,
      dealStatus: sharedFilters.dealStatus,
      paymentType: sharedFilters.paymentType,
    }),
    [groupBy, sharedFilters]
  );

  const { rows, loading } = useFinancePerformanceData(filters, { key: sortKey, value: sortDir });

  const handleSort = (key: PerformanceSortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <PerformanceFilters
        filters={filters}
        onChange={newFilters => {
          if (newFilters.groupBy !== groupBy) setGroupBy(newFilters.groupBy);
          onSharedFiltersChange({
            dateRange: newFilters.dateRange,
            dealStatus: newFilters.dealStatus,
            paymentType: newFilters.paymentType,
          });
        }}
      />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <PerformanceKpis rows={rows} />
          <PerformanceTable rows={rows} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
        </>
      )}
      <Stack direction="row" spacing={2} mt={1.5} flexWrap="wrap">
        {[
          ['Closed won', 'terminal won status (Funded)'],
          ['Pipeline', 'all open, excl. Dead/Lost/Cancelled'],
          ['Financing', 'payment type finance'],
          ['Cash', 'cash buyer'],
          ['Avg / deal (all)', 'profit / total deals'],
          ['Avg / deal (F&I)', 'profit / non-zero revenue deals'],
        ].map(([label, desc]) => (
          <Typography key={label} variant="caption" color="text.disabled">
            <strong style={{ color: '#888' }}>{label}</strong> = {desc}
          </Typography>
        ))}
      </Stack>
      <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
        Rank badges follow active sort column. Penetration bars proportional to column max (green &gt;= 70%, amber
        40-69%, red &lt; 40%).
      </Typography>
    </Box>
  );
};
